import { fullName } from '../ReGexs';

export const USER_STATUS_LABEL_KEYS = Object.freeze({
  A: 'com.tempedge.msg.label.active',
  P: 'com.tempedge.msg.label.pending',
  D: 'com.tempedge.msg.label.deleted',
  R: 'com.tempedge.msg.label.rejected'
});

/**
 * Common request envelope for /api/v2/user org-access endpoints.
 * @see user-org-access-api.md
 */
export const buildOrgAccessRequest = (fields = {}) => {
  let agency = {};
  try {
    agency = JSON.parse(localStorage.getItem('agency') || '{}');
  } catch {
    agency = {};
  }

  const userName = agency?.user?.username || agency?.user?.userName || agency?.username || agency?.userName || '';
  const orgId = agency?.organizationEntity?.orgId;

  return {
    transactionId: `tx-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    userName,
    ...(orgId != null ? { orgId: Number(orgId) } : {}),
    ...fields
  };
};

export const getOrgAccessOrgId = (org) => org?.organizationEntity?.orgId ?? org?.orgId ?? null;

export const getOrgAccessOrgName = (org) => org?.organizationEntity?.organizationName || org?.orgName || org?.officeName || '---';

export const getOrgAccessRoleName = (org) => org?.roleName || org?.userRoleName || org?.user?.roles?.[0]?.name || '---';

export const getOrgAccessUserId = (record) => record?.userId ?? record?.user?.id ?? null;

/**
 * Builds the user identity ({ userId, username, name, email }) from an org-access
 * record (flat DTO or nested user), with optional fallback data (e.g. the row
 * that opened the modal).
 */
export const buildOrgAccessIdentity = (record, fallback = {}) => ({
  userId: getOrgAccessUserId(record) ?? fallback.userId ?? fallback.user?.id ?? null,
  username: record?.username ?? record?.user?.username ?? fallback.username ?? null,
  name: fullName(
    record?.firstName ?? record?.user?.firstName ?? fallback.firstName,
    (record?.user?.middleName ?? record?.middleName ?? fallback.middleName) || null,
    record?.lastName ?? record?.user?.lastName ?? fallback.lastName
  ),
  email: record?.email ?? record?.user?.email ?? fallback.email ?? null
});

/**
 * Builds an <img> src from a raw logo value (data URI, absolute URL, path or bare base64).
 * JPEG base64 starts with "/9j/", so that prefix is checked before treating "/" as a path.
 */
export const buildOrgLogoSrc = (logo) => {
  if (!logo || typeof logo !== 'string') return null;
  const trimmed = logo.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:')) return trimmed;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('/9j/')) return `data:image/jpeg;base64,${trimmed}`;
  if (trimmed.startsWith('iVBOR')) return `data:image/png;base64,${trimmed}`;
  if (/^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 100) {
    return `data:image/png;base64,${trimmed}`;
  }
  return trimmed.startsWith('/') ? trimmed : `data:image/png;base64,${trimmed}`;
};

/**
 * Maps findByUsername DTO (flat) + legacy portalUserConf into a list the UI can render.
 */
export const normalizeOrgAccessList = (rows = []) =>
  rows.map((row) => ({
    ...row,
    userRoleName: row.roleName || row.userRoleName,
    clientId: row.clientId ?? null,
    officeId: row.officeId ?? null,
    organizationEntity: row.organizationEntity
      ? {
          ...row.organizationEntity,
          logo: row.organizationEntity.logo || row.logo || null
        }
      : {
          orgId: row.orgId,
          organizationName: row.orgName,
          logo: row.logo || null
        },
    user: row.user || {
      id: row.userId,
      username: row.username,
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email
    }
  }));

/** Attach logos from agency/findAll (findByUsername does not return logo). */
export const enrichOrgAccessWithLogos = (rows = [], logoByOrgId = {}) =>
  rows.map((row) => {
    const orgId = getOrgAccessOrgId(row);
    const existingLogo = row.organizationEntity?.logo || row.logo;
    const logo = existingLogo || logoByOrgId[orgId] || null;
    if (!logo) return row;
    return {
      ...row,
      organizationEntity: {
        ...(row.organizationEntity || { orgId, organizationName: getOrgAccessOrgName(row) }),
        logo
      }
    };
  });
